/**
 * Nutrient-limit calc — mirrors the mobile services/kidney/nutrientLimits.ts and
 * the funnel's calculateAppNutrientLimits. Hypertension / heart disease subtract
 * 300mg sodium each (floor 1000mg).
 */

import { isDialysisCondition, isPeritonealType, isPreDialysisType } from '../../../shared/dialysis'

export interface NutrientLimitResult {
  sodium: number
  potassium: number
  phosphorus: number
  proteinPerKg: number
  proteinRange: string
}

// Mobile app/settings/kidney-status.tsx STAGE_OPTIONS — the exact editor set,
// so a condition chosen on either platform round-trips on both.
export const KIDNEY_CONDITIONS: { value: string; labelKey: string; fallback: string }[] = [
  { value: 'ckd_stage_1', labelKey: 'kidneyStatus.stages.stage1', fallback: 'CKD Stage 1' },
  { value: 'ckd_stage_2', labelKey: 'kidneyStatus.stages.stage2', fallback: 'CKD Stage 2' },
  { value: 'ckd_stage_3a', labelKey: 'kidneyStatus.stages.stage3a', fallback: 'CKD Stage 3a' },
  { value: 'ckd_stage_3b', labelKey: 'kidneyStatus.stages.stage3b', fallback: 'CKD Stage 3b' },
  { value: 'ckd_stage_4', labelKey: 'kidneyStatus.stages.stage4', fallback: 'CKD Stage 4' },
  { value: 'ckd_stage_5', labelKey: 'kidneyStatus.stages.stage5', fallback: 'CKD Stage 5' },
  { value: 'on_dialysis', labelKey: 'kidneyStatus.stages.onDialysis', fallback: 'On Dialysis' },
  { value: 'transplant', labelKey: 'kidneyStatus.stages.transplant', fallback: 'Transplant' },
  { value: 'kidney_stones', labelKey: 'kidneyStatus.stages.kidneyStones', fallback: 'Kidney Stones' },
  { value: 'no_diagnosis', labelKey: 'kidneyStatus.stages.noDiagnosis', fallback: 'No Diagnosis' },
  { value: 'not_sure', labelKey: 'kidneyStatus.stages.notSure', fallback: 'Not Sure' },
]

export const COMORBIDITIES: { value: string; labelKey: string; fallback: string }[] = [
  { value: 'high_blood_pressure', labelKey: 'settings.comorbidities.highBloodPressure', fallback: 'High blood pressure' },
  { value: 'heart_disease', labelKey: 'settings.comorbidities.heartDisease', fallback: 'Heart disease' },
  { value: 'diabetes', labelKey: 'settings.comorbidities.diabetes', fallback: 'Diabetes' },
  // 4.47: gout, anemia, bone/mineral disorder (mobile parity; they change no limit)
  { value: 'gout', labelKey: 'settings.comorbidities.gout', fallback: 'Gout' },
  { value: 'anemia', labelKey: 'settings.comorbidities.anemia', fallback: 'Anemia' },
  { value: 'bone_disease', labelKey: 'settings.comorbidities.boneDisease', fallback: 'Bone/mineral disorder' },
]

/** Map a stored condition value to its settings.ckdStages.* translation key. */
export function conditionLabelKey(condition?: string): { key: string; fallback: string } {
  const found = KIDNEY_CONDITIONS.find((c) => c.value === condition)
  if (found) return { key: found.labelKey, fallback: found.fallback }
  if (condition === 'ckd_stage_3') return { key: 'settings.ckdStages.stage3', fallback: 'CKD Stage 3' }
  return { key: 'settings.ckdStages.prevention', fallback: condition || '—' }
}

export function calculateNutrientLimits(
  condition: string,
  comorbidities: string[] = [],
  dialysisType?: string,
): NutrientLimitResult {
  let limits: NutrientLimitResult

  // Verbatim port of mobile services/kidney/nutrientLimits.ts — every case,
  // including legacy and onboarding values. Divergence here CORRUPTS the
  // shared kidneyProfile.calculatedLimits both platforms consume.
  // 4.46 F7a: a dialysis status + "Starting soon" (`not_started`) gets the Stage 5
  // (not on dialysis) row (parity: __tests__/startingSoon446.test.ts).
  const startingSoon = isDialysisCondition(condition) && isPreDialysisType(dialysisType)
  switch (startingSoon ? 'ckd_stage_5' : condition) {
    case 'ckd_stage_1':
    case 'ckd_stage_2':
    case 'ckd_stage_1_2': // Legacy value
      limits = { sodium: 2300, potassium: 3000, phosphorus: 1000, proteinPerKg: 0.8, proteinRange: '0.8g/kg' }
      break
    case 'ckd_stage_3a':
    case 'ckd_stage_3b':
    case 'ckd_stage_3': // Legacy value
      limits = { sodium: 2000, potassium: 2500, phosphorus: 800, proteinPerKg: 0.7, proteinRange: '0.6-0.8g/kg' }
      break
    case 'ckd_stage_4':
    case 'ckd_stage_5':
    case 'ckd_stage_4_5': // Legacy value
      limits = { sodium: 1500, potassium: 2000, phosphorus: 700, proteinPerKg: 0.6, proteinRange: '0.6g/kg' }
      break
    // Stage 5 with dialysis: higher protein needs
    case 'ckd_stage_5_dialysis':
    case 'dialysis':
    case 'on_dialysis': // From onboarding
      limits = { sodium: 2000, potassium: 2500, phosphorus: 1000, proteinPerKg: 1.1, proteinRange: '1.0-1.2g/kg' }
      break
    // Transplant: generally more relaxed restrictions
    case 'transplant':
      limits = { sodium: 2300, potassium: 3000, phosphorus: 1000, proteinPerKg: 0.8, proteinRange: '0.8g/kg' }
      break
    // Kidney stones + prevention/default
    case 'kidney_stones':
    case 'not_sure_prevention':
    case 'at_risk':
    case 'no_diagnosis':
    case 'not_sure':
    default:
      limits = { sodium: 2300, potassium: 4000, phosphorus: 1000, proteinPerKg: 0.8, proteinRange: '0.8g/kg' }
  }

  // 4.47 M7b: peritoneal dialysis keeps the dialysis row except potassium 3,000 mg
  // (mobile PD_POTASSIUM_MG; parity: __tests__/pdLimits447.test.ts).
  if (isDialysisCondition(condition) && isPeritonealType(dialysisType)) limits.potassium = 3000

  if (comorbidities.includes('high_blood_pressure')) limits.sodium -= 300
  if (comorbidities.includes('heart_disease')) limits.sodium -= 300
  limits.sodium = Math.max(1000, limits.sodium)

  return limits
}
